import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { Navigate } from "react-router-dom";
// import { Helmet } from "react-helmet-async";
import { Formik } from "formik";
import axios from "axios";
import * as moment from "moment";
import {
  Alert as MuiAlert,
  Button as MuiButton,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField as MuiTextField,
  Typography,
  FormControl as MuiFormControl,
} from "@mui/material";
import { spacing } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import TaxOfficeSelect from "../../components/TaxOfficeSelect";
import { isGermanyCountry } from "../../utils/taxOfficeUtils";

const Card = styled(MuiCard)(spacing);

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

const Button = styled(MuiButton)(spacing);

const FormControlSpacing = styled(MuiFormControl)(spacing);

const FormControl = styled(FormControlSpacing)`
  min-width: 148px;
`;

const timeOut = (time) => new Promise((res) => setTimeout(res, time));

const initialValues = {
  taxOfficeId: "",
  steuerNummer: "",
};

const createValidationSchema = (isGermany) =>
  Yup.object().shape({
    entityNumber: Yup.string().required("Required"),
    companyName: Yup.string().required("Required"),
    companyAddress: Yup.string().required("Required"),
    companyPostalCode: Yup.string().required("Required"),
    companyCity: Yup.string().required("Required"),
    companyProvince: Yup.string().required("Required"),
    coc: Yup.string().required("Required"),
    vat: Yup.string().required("Required"),
    steuerNummer: isGermany ? Yup.string().trim().required("Required") : Yup.string(),
    taxOfficeId: Yup.number().required("Required").typeError("Required"),
    website: Yup.string().required("Required"),
    email: Yup.string().email().required("Required"),
    phonenumber: Yup.number().required("Required"),
    startupDate: Yup.date().required("Required"),
  });

export const AddCustomerForm = ({ onSuccess, inDialog = false }) => {
  const [countryData, setCountryData] = useState([]);
  const [isPoland, setIsPoland] = useState(false);
  const [selectRelationType, setRelationType] = React.useState("");
  const [selectCountry, setCountry] = React.useState("");
  const isGermany = isGermanyCountry(selectCountry, countryData);

  useEffect(() => {
    axios.get("/api/v1/countries").then((response) => setCountryData(response.data));
  }, []);

  const [relationTypeData, setRelationTypeData] = useState([]);

  useEffect(() => {
    axios.get("/api/v1/relationTypes").then((response) => setRelationTypeData(response.data));
  }, []);

  const [openAlert, setOpenAlert] = React.useState(false);
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [alertMessage, setAlertMessage] = useState("");

  const handleSubmit = async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
    try {
      await timeOut(500);
      const test = {
        entityNumber: values.entityNumber,
        typeRelation: selectRelationType,
        name: values.companyName,
        address: values.companyAddress,
        houseNumber: isPoland ? values.houseNumber : "",
        postalCode: values.companyPostalCode,
        city: values.companyCity,
        province: values.companyProvince,
        county: values.companyCounty || "",
        municipality: values.companyMunicipality || "",
        postOffice: values.postOffice || "",
        countryId: selectCountry,
        coC: values.coc,
        vat: values.vat,
        steuerNummer: isGermany ? values.steuerNummer : "",
        taxOfficeId: values.taxOfficeId,
        website: values.website,
        generalEmailAddress: values.email,
        generalPhoneNumber: values.phonenumber,
        supplierId: 0,
        creationDate: moment().format("YYYY-MM-DDThh:mm:ss.SSS") + "Z",
        startupDate: values.startupDate,
        endDate: null,
        modifiedDate: moment().format("YYYY-MM-DDThh:mm:ss.SSS") + "Z",
        isDisabled: true,
        entityStatusId: 0,
      };

      axios
        .post("/api/v1/entities", test)
        .then((response) => {
          setOpenAlert(true);
          setAlertSeverity("success");
          setAlertMessage("Customer has been created successfully");
          setTimeout(() => {
            setOpenAlert(false);
          }, 5000);
          onSuccess?.();
        })
        .catch((error) => {
          if (error.response) {
            setOpenAlert(true);
            setAlertSeverity("error");
            setAlertMessage("Customer creation failed!");
            setTimeout(() => {
              setOpenAlert(false);
            }, 10000);
          }
        });

      resetForm();
      setStatus({ sent: true });
      setSubmitting(false);
    } catch (error) {
      setStatus({ sent: false });
      setErrors({ submit: error.message });
      setSubmitting(false);
    }
  };

  const handleSelectChange = (event, setFieldValue) => {
    if (event.target.name === "relationType") {
      setRelationType(event.target.value);
    }

    if (event.target.name === "companyCountry") {
      setIsPoland(event.target.value === 17);
      setCountry(event.target.value);
      setFieldValue?.("taxOfficeId", "");
      if (!isGermanyCountry(event.target.value, countryData)) {
        setFieldValue?.("steuerNummer", "");
      }
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={createValidationSchema(isGermany)}
      onSubmit={handleSubmit}
      //onChange={handleChange}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values, status }) => {
        const formContent = (
          <>
            {!inDialog && (
              <Typography variant="h6" gutterBottom>
                New Entity Information
              </Typography>
            )}

            <Snackbar
              open={openAlert}
              autoHideDuration={5000}
              onClose={() => setOpenAlert(false)}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Alert
                onClose={() => setOpenAlert(false)}
                severity={alertSeverity}
                sx={{ width: "100%" }}
              >
                {alertMessage}
              </Alert>
            </Snackbar>

            <form onSubmit={handleSubmit}>
              <Typography variant="body2" gutterBottom>
                Entity Information
              </Typography>
              <Grid container spacing={6}>
                <Grid item md={4} xs={12}>
                  <TextField
                    name="entityNumber"
                    label="Customer Number"
                    value={values.entityNumber}
                    error={Boolean(touched.entityNumber && errors.entityNumber)}
                    fullWidth
                    helperText={touched.entityNumber && errors.entityNumber}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="entityNumber"
                    variant="outlined"
                    my={2}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    name="companyName"
                    label="Company Name"
                    value={values.companyName}
                    error={Boolean(touched.companyName && errors.companyName)}
                    fullWidth
                    helperText={touched.companyName && errors.companyName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="companyName"
                    variant="outlined"
                    my={2}
                  />
                </Grid>
                <Grid item md={2} xs={12}>
                  <FormControl fullWidth my={2}>
                    <InputLabel id="demo-simple-select-label">Relation Type</InputLabel>
                    <Select
                      name="relationType"
                      labelId="relationType"
                      id="relationType-select"
                      value={selectRelationType}
                      label="Relation Type"
                      error={Boolean(touched.relationType && errors.relationType)}
                      // helperText={touched.relationType && errors.relationType}
                      onBlur={handleBlur}
                      onChange={(event) => handleSelectChange(event, setFieldValue)}
                    >
                      {relationTypeData.map(({ relationTypeId, shortDescription }, index) => (
                        <MenuItem key={relationTypeId} value={relationTypeId}>
                          {shortDescription}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              {isPoland ? (
                <Grid container spacing={6}>
                  <Grid item md={4} xs={12}>
                    <TextField
                      name="companyAddress"
                      label="Street"
                      value={values.companyAddress}
                      error={Boolean(touched.companyAddress && errors.companyAddress)}
                      fullWidth
                      helperText={touched.companyAddress && errors.companyAddress}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      my={2}
                    />
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <TextField
                      name="houseNumber"
                      label="House Number"
                      value={values.houseNumber}
                      error={Boolean(touched.houseNumber && errors.houseNumber)}
                      fullWidth
                      helperText={touched.houseNumber && errors.houseNumber}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      my={2}
                    />
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <TextField
                      name="companyPostalCode"
                      label="Postal Code"
                      value={values.companyPostalCode}
                      error={Boolean(touched.companyPostalCode && errors.companyPostalCode)}
                      fullWidth
                      helperText={touched.companyPostalCode && errors.companyPostalCode}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      my={2}
                    />
                  </Grid>
                  <Grid item md={4} xs={12}>
                    <TextField
                      name="companyCity"
                      label="City"
                      value={values.companyCity}
                      error={Boolean(touched.companyCity && errors.companyCity)}
                      fullWidth
                      helperText={touched.companyCity && errors.companyCity}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      my={2}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={6}>
                  <Grid item md={8} xs={12}>
                    <TextField
                      name="companyAddress"
                      label="Address"
                      value={values.companyAddress}
                      error={Boolean(touched.companyAddress && errors.companyAddress)}
                      fullWidth
                      helperText={touched.companyAddress && errors.companyAddress}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      my={2}
                    />
                  </Grid>
                  <Grid item md={4} xs={12}>
                    <TextField
                      name="companyPostalCode"
                      label="Postal Code"
                      value={values.companyPostalCode}
                      error={Boolean(touched.companyPostalCode && errors.companyPostalCode)}
                      fullWidth
                      helperText={touched.companyPostalCode && errors.companyPostalCode}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      my={2}
                    />
                  </Grid>
                </Grid>
              )}
              {isPoland ? (
                <Grid container spacing={6}>
                  <Grid item md={3} xs={12}>
                    <TextField
                      name="companyProvince"
                      label="Province"
                      value={values.companyProvince}
                      error={Boolean(touched.companyProvince && errors.companyProvince)}
                      fullWidth
                      helperText={touched.companyProvince && errors.companyProvince}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      my={2}
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <TextField
                      name="companyCounty"
                      label="County"
                      value={values.companyCounty}
                      error={Boolean(touched.companyCounty && errors.companyCounty)}
                      fullWidth
                      helperText={touched.companyCounty && errors.companyCounty}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      my={2}
                    />
                  </Grid>
                  <Grid item md={4} xs={12}>
                    <TextField
                      name="companyMunicipality"
                      label="Municipality"
                      value={values.companyMunicipality}
                      error={Boolean(touched.companyMunicipality && errors.companyMunicipality)}
                      fullWidth
                      helperText={touched.companyMunicipality && errors.companyMunicipality}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      my={2}
                    />
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <FormControl fullWidth my={2}>
                      <InputLabel id="demo-simple-select-label">Country</InputLabel>
                      <Select
                        name="companyCountry"
                        labelId="companyCountry"
                        id="companyCountry-select"
                        value={selectCountry}
                        label="Country"
                        error={Boolean(touched.companyCountry && errors.companyCountry)}
                        // helperText={
                        //   touched.companyCountry && errors.companyCountry
                        // }
                        onBlur={handleBlur}
                        onChange={(event) => handleSelectChange(event, setFieldValue)}
                      >
                        {countryData.map(({ countryId, countryCode }, index) => (
                          <MenuItem key={countryId} value={countryId}>
                            {countryCode}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={6}>
                  <Grid item md={5} xs={12}>
                    <TextField
                      name="companyCity"
                      label="City"
                      value={values.companyCity}
                      error={Boolean(touched.companyCity && errors.companyCity)}
                      fullWidth
                      helperText={touched.companyCity && errors.companyCity}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      my={2}
                    />
                  </Grid>
                  <Grid item md={5} xs={12}>
                    <TextField
                      name="companyProvince"
                      label="Province"
                      value={values.companyProvince}
                      error={Boolean(touched.companyProvince && errors.companyProvince)}
                      fullWidth
                      helperText={touched.companyProvince && errors.companyProvince}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      my={2}
                    />
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <FormControl fullWidth my={2}>
                      <InputLabel id="demo-simple-select-label">Country</InputLabel>
                      <Select
                        name="companyCountry"
                        labelId="companyCountry"
                        id="companyCountry-select"
                        value={selectCountry}
                        label="Country"
                        error={Boolean(touched.companyCountry && errors.companyCountry)}
                        // helperText={
                        //   touched.companyCountry && errors.companyCountry
                        // }
                        onBlur={handleBlur}
                        onChange={(event) => handleSelectChange(event, setFieldValue)}
                      >
                        {countryData.map(({ countryId, countryCode }, index) => (
                          <MenuItem key={countryId} value={countryId}>
                            {countryCode}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
              <Grid container spacing={6} variant="outlined">
                <Grid item md={6} xs={12}>
                  <TextField
                    name="email"
                    label="General Email Address"
                    value={values.email}
                    error={Boolean(touched.email && errors.email)}
                    fullWidth
                    helperText={touched.email && errors.email}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="email"
                    variant="outlined"
                    my={2}
                  />
                </Grid>

                <Grid item md={6} xs={12}>
                  <TextField
                    name="phonenumber"
                    label="General Phone Number"
                    value={values.phonenumber}
                    error={Boolean(touched.phonenumber && errors.phonenumber)}
                    fullWidth
                    helperText={touched.phonenumber && errors.phonenumber}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="phone"
                    variant="outlined"
                    my={2}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={6} variant="outlined">
                <Grid item md={isGermany ? 4 : 6} xs={12}>
                  <TextField
                    name="coc"
                    label="Chamber of Commerce"
                    value={values.coc}
                    error={Boolean(touched.coc && errors.coc)}
                    fullWidth
                    helperText={touched.coc && errors.coc}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="coc"
                    variant="outlined"
                    my={2}
                  />
                </Grid>
                <Grid item md={isGermany ? 4 : 6} xs={12}>
                  <TextField
                    name="vat"
                    label="VAT"
                    value={values.vat}
                    error={Boolean(touched.vat && errors.vat)}
                    fullWidth
                    helperText={touched.vat && errors.vat}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="vat"
                    variant="outlined"
                    my={2}
                  />
                </Grid>
                {isGermany ? (
                  <Grid item md={4} xs={12}>
                    <TextField
                      name="steuerNummer"
                      label="Steuernummer"
                      value={values.steuerNummer}
                      error={Boolean(touched.steuerNummer && errors.steuerNummer)}
                      fullWidth
                      helperText={touched.steuerNummer && errors.steuerNummer}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      inputProps={{ "aria-label": "Steuernummer" }}
                      variant="outlined"
                      my={2}
                    />
                  </Grid>
                ) : null}
              </Grid>
              {isPoland ? (
                <Grid container spacing={6} variant="outlined">
                  <Grid item md={6} xs={12}>
                    <TaxOfficeSelect
                      value={values.taxOfficeId}
                      countryId={selectCountry}
                      error={touched.taxOfficeId && errors.taxOfficeId}
                      helperText={touched.taxOfficeId && errors.taxOfficeId}
                      onBlur={handleBlur}
                      onChange={(event) => setFieldValue("taxOfficeId", event.target.value)}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField
                      name="postOffice"
                      label="Post Office"
                      value={values.postOffice}
                      error={Boolean(touched.postOffice && errors.postOffice)}
                      fullWidth
                      helperText={touched.postOffice && errors.postOffice}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="postOffice"
                      variant="outlined"
                      my={2}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={6} variant="outlined">
                  <Grid item md={12} xs={12}>
                    <TaxOfficeSelect
                      value={values.taxOfficeId}
                      countryId={selectCountry}
                      error={touched.taxOfficeId && errors.taxOfficeId}
                      helperText={touched.taxOfficeId && errors.taxOfficeId}
                      onBlur={handleBlur}
                      onChange={(event) => setFieldValue("taxOfficeId", event.target.value)}
                    />
                  </Grid>
                </Grid>
              )}
              <Grid container spacing={6} variant="outlined">
                <Grid item md={6} xs={12}>
                  <TextField
                    name="website"
                    label="Website"
                    value={values.website}
                    error={Boolean(touched.website && errors.website)}
                    fullWidth
                    helperText={touched.website && errors.website}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="website"
                    variant="outlined"
                    my={2}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    id="startupDate"
                    name="startupDate"
                    label="Start Date"
                    type="date"
                    value={values.startupDate} // Use value instead of defaultValue
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    my={2}
                  />
                </Grid>
              </Grid>
              <Button
                disabled={isSubmitting}
                type="submit"
                variant="contained"
                color="primary"
                mt={3}
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ marginRight: "10px" }}>Save New Entity</span>
                {isSubmitting && <CircularProgress size={20} />}
              </Button>
            </form>
          </>
        );

        if (inDialog) {
          return formContent;
        }

        return (
          <Card mb={6}>
            <CardContent>{formContent}</CardContent>
          </Card>
        );
      }}
    </Formik>
  );
};

export const AddCustomerDialog = ({ open, onClose, onSuccess }) => {
  const handleSuccess = () => {
    setTimeout(() => {
      onSuccess?.();
      onClose();
    }, 1500);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      aria-labelledby="add-entity-dialog-title"
    >
      <DialogTitle
        id="add-entity-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Add Entity
        <IconButton aria-label="close add entity dialog" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {open && <AddCustomerForm key="add-customer-form" onSuccess={handleSuccess} inDialog />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

const AddCustomerPage = () => <Navigate to="/customers/list" replace />;

export default AddCustomerPage;
